import DayPickerRenderer from './DayPickerRenderer';
import { observer } from 'mobx-react-lite';
import { logUsage } from 'owa-analytics';
import { Module } from 'owa-bootstrap';
import {
    CalendarModuleHamburgerButton,
    CalendarModuleNewEventButton,
} from 'owa-calendar-commandbar';
import { isBoardViewTypeSelected } from 'owa-calendar-module-store';
import { CalendarPicker, CalendarPickerScenarioId } from 'owa-calendar-picker';
import { getSearchState } from 'owa-calendar-search-view-store';
import { isFeatureEnabled } from 'owa-feature-flags';
import { isHostAppFeatureEnabled } from 'owa-hostapp-feature-flags';
import { isLeftRailVisible } from 'owa-left-rail-utils/lib/isLeftRailVisible';
import isOfficeRailEnabled from 'owa-left-rail-utils/lib/isOfficeRailEnabled';
import loc, { format } from 'owa-localize';
import { jumpToRegion } from 'owa-locstrings/lib/strings/jumptoregion.locstring.json';
import { ModuleSwitcher } from 'owa-module-switcher';
import { lazyMountAndShowFullOptions } from 'owa-options-view';
import { exitSearch, getScenarioStore, SearchScenarioId } from 'owa-search';
import { SkipLinkRegion } from 'owa-skip-link/lib/components/SkipLinkRegion';
import SkipLinkRegionToFirstFocusableChild from 'owa-skip-link/lib/components/SkipLinkRegionToFirstFocusableChild';
import * as React from 'react';
import {
    CalendarLayoutLeftPaneConstants,
    calculateCalendarsListPaneWidth,
    getLeftNavWidth,
    getLeftPaneMinWidth,
    shouldShowLeftNavAsOverlay,
    getIsLeftNavShown,
} from 'owa-calendar-layout';
import {
    dateSelectionRegionName,
    calendarListRegionName,
    moduleSwitcherRegionName,
} from './CalendarModule.locstring.json';
import {
    lazyOpenAddCalendarsPane,
    lazyNavigateToAddCalendarPanel,
    AddCalendarsMenuItemId,
} from 'owa-calendar-addcalendar';

const styles = require('./CalendarLeftPane.scss');

interface CalendarLeftPaneProps {
    leftNavWidthOverride?: number;
    toggleHamburgerButton: () => void;
    shouldAutoFocusHamburgerButton: boolean;
}

export default observer(function CalendarLeftPane(props: CalendarLeftPaneProps) {
    const leftPaneMinWidth = getLeftPaneMinWidth();
    const calendarSidePanelIsExpanded = getIsLeftNavShown();
    const isCloudCacheMultiAccountRail =
        isLeftRailVisible(Module.Calendar) && !isOfficeRailEnabled(Module.Calendar);

    const isSurfaceViewType = !isBoardViewTypeSelected();

    return (
        <div className={styles.mainContent}>
            {calendarSidePanelIsExpanded && (
                <div
                    className={styles.leftPane}
                    style={{
                        width: props.leftNavWidthOverride ?? calculateCalendarsListPaneWidth(),
                        minWidth: leftPaneMinWidth,
                    }}
                    data-min-width={leftPaneMinWidth}
                    data-max-width={CalendarLayoutLeftPaneConstants.leftPaneMaxWidth}
                    onClick={onLeftPaneClick}>
                    {!shouldShowLeftNavAsOverlay() && (
                        <div className={styles.leftPaneFirstRow}>
                            {!isCloudCacheMultiAccountRail && (
                                <>
                                    <CalendarModuleHamburgerButton
                                        autoFocus={props.shouldAutoFocusHamburgerButton}
                                        isPaneExpanded={calendarSidePanelIsExpanded}
                                        toggleHamburgerButton={props.toggleHamburgerButton}
                                    />
                                    <div className={styles.leftPaneCommandBarWrapper}>
                                        <CalendarModuleNewEventButton />
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                    <SkipLinkRegion
                        skipLinkName={format(loc(jumpToRegion), loc(dateSelectionRegionName))}
                        role="navigation"
                        className={styles.dayPicker}
                        regionName={loc(dateSelectionRegionName)}>
                        <DayPickerRenderer />
                    </SkipLinkRegion>
                    <SkipLinkRegion
                        skipLinkName={format(loc(jumpToRegion), loc(calendarListRegionName))}
                        role="complementary"
                        regionName={loc(calendarListRegionName)}
                        className={styles.calendarPickerContainer}>
                        <CalendarPicker
                            scenarioId={CalendarPickerScenarioId.SurfaceLeftPane}
                            className={styles.calendarPicker}
                            showCalendarPicker={isSurfaceViewType}
                            onClickAddCalendar={openAddCalendarsClicked}
                            onClickAccountSettings={accountSettingsOnClick}
                            onClickAddTeamsCalendar={openAddTeamsCalendarsClicked}
                        />
                    </SkipLinkRegion>
                    {!isFeatureEnabled('tri-officeRail') && (
                        <div className={styles.moduleSwitcher}>
                            {!isLeftRailVisible(Module.Calendar) &&
                                isHostAppFeatureEnabled('calendarOfficeRail') && (
                                    <SkipLinkRegionToFirstFocusableChild
                                        role="navigation"
                                        regionNameResourceId={moduleSwitcherRegionName}>
                                        <ModuleSwitcher
                                            selectedModule={Module.Calendar}
                                            containerWidth={
                                                props.leftNavWidthOverride ?? getLeftNavWidth()
                                            }
                                        />
                                    </SkipLinkRegionToFirstFocusableChild>
                                )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
});

function onLeftPaneClick() {
    if (getSearchState() || getScenarioStore(SearchScenarioId.Calendar).searchSessionGuid) {
        exitSearch('LeftNavClick', SearchScenarioId.Calendar);
    }
}
/**
 * Called when 'Add calendars' gets clicked
 */
function openAddCalendarsClicked() {
    lazyOpenAddCalendarsPane.importAndExecute('CalendarSurfaceLeftNav');
}

/**
 * Called when 'Add Teams calendars' gets clicked
 */
async function openAddTeamsCalendarsClicked() {
    await lazyOpenAddCalendarsPane.importAndExecute('TeamsCalendarGroupHeader');
    lazyNavigateToAddCalendarPanel.importAndExecute(AddCalendarsMenuItemId.TeamsCalendars);
}

/**
 * Called when 'Account settings' is clicked from the account header context menu
 */
function accountSettingsOnClick() {
    logUsage('accountSettingsClicked', { source_1: 'accountContextMenu' });
    lazyMountAndShowFullOptions.importAndExecute('calendar', 'calAccounts');
}
